// complex-dice.ts — the sidelog "complex dice" picker (d4/d6/d8/d10/d12/d20 count steppers), carved out of
// template.js's integration tail (Phase 2h of plans/template-js-carve-up.md) and TS-ported for Phase 2a of
// plans/bundler-script-load-order-phase2.md — the FIRST Wave-2a fold into the post-template-tail aggregate.
//
// Behavior port of includes/js/complex-dice-init.js: tap a die = +1 (committed after a short delay so a
// double-tap can show −1 without flicker); double-tap / second tap before commit = −1; middle-click or
// long-press = clear that die; the clear button zeroes all. ftApplyComplexDieValue clamps + mirrors the
// count into the visible badge. (The actual roll is fired by rollComplexDice in roll-init.js, which reads
// the die_*_dice inputs this wires.)
//
// SELF-CONTAINED — no template.js deps: diceConfig + the FT_COMPLEX_DIE_* timing consts are defined here.
// The event-wiring runs at module-execute time over the sidelog die_* / #btn_clear_complex_dice elements
// (rendered above this script in the footer), so they're present by then. Null-guards were ADDED on the DOM
// lookups (the original accessed #btn_clear_complex_dice unguarded and would throw on pages without the
// sidelog) so the module is safe to share an aggregate IIFE with future post-template-tail members.
//
// PUBLIC GLOBAL (window-attached at the bottom): ftApplyComplexDieValue (exported for parity / external reuse).

interface DieConfig {
    max: number;
}

// Define dice configurations
const diceConfig: Record<string, DieConfig> = {
    d4:  { max: 100 },
    d6:  { max: 100 },
    d8:  { max: 100 },
    d10: { max: 100 },
    d12: { max: 120 },
    d20: { max: 200 },
};

// Complex die picker: primary tap commits +1 after a short delay so double-tap can show −1 with no flicker;
// double-click / second tap before commit = −1 from count at start of the pair;
// middle-click = clear this die only; long-press = clear this die.
// (Right-click is intentionally not bound — the native browser context menu is left to the browser.)
const FT_COMPLEX_DIE_SINGLE_TAP_COMMIT_MS = 310;
const FT_COMPLEX_DIE_LONG_PRESS_MS = 480;

function ftApplyComplexDieValue(inputElement: HTMLInputElement, showElement: HTMLElement, maxValue: number): void {
    let val = parseInt(inputElement.value, 10) || 0;
    if (val < 0) val = 0;
    if (val > maxValue) val = maxValue;
    inputElement.value = String(val);
    showElement.textContent = val > 0 ? String(val) : '';
}

// Attach event listener to all dice elements
Object.keys(diceConfig).forEach((diceType) => {
    const dieElement = document.getElementById(`die_${diceType}`);
    if (!dieElement) return;

    const inputId = `die_${diceType.replace('d', '')}_dice`;
    const showId = `${inputId}_show`;
    const inputElement = document.getElementById(inputId) as HTMLInputElement | null;
    const showElement = document.getElementById(showId);
    if (!inputElement || !showElement) return;
    const config = diceConfig[diceType];
    if (!config) return;
    const maxValue = config.max;

    let longPressTimer: number | null = null;
    let holdClearConsumedClick = false;
    let singleTapCommitTimer: number | null = null;
    let pendingTapBase: number | null = null;

    function clearLongPressTimer() {
        if (longPressTimer !== null) {
            clearTimeout(longPressTimer);
            longPressTimer = null;
        }
    }

    function clearSingleTapCommit() {
        if (singleTapCommitTimer !== null) {
            clearTimeout(singleTapCommitTimer);
            singleTapCommitTimer = null;
        }
        pendingTapBase = null;
    }

    // Middle-click clears this die only; left uses bubble click + commit timer elsewhere.
    // Right-click is intentionally not handled — browser context menu is left alone.
    dieElement.addEventListener('mousedown', (event) => {
        if (event.button !== 1) return;
        event.preventDefault();
        clearSingleTapCommit();
        inputElement.value = '0';
        ftApplyComplexDieValue(inputElement, showElement, maxValue);
    }, true);

    dieElement.addEventListener('click', (event) => {
        if (event.button !== 0) return;
        if (holdClearConsumedClick) {
            holdClearConsumedClick = false;
            event.preventDefault();
            event.stopPropagation();
            return;
        }

        const secondOfPair =
            event.detail === 2
            || (singleTapCommitTimer !== null && pendingTapBase !== null);

        if (secondOfPair) {
            clearTimeout(singleTapCommitTimer ?? undefined);
            singleTapCommitTimer = null;
            const base = pendingTapBase;
            pendingTapBase = null;
            if (base !== null) {
                inputElement.value = String(base - 1);
            } else {
                const val = parseInt(inputElement.value, 10) || 0;
                inputElement.value = String(val - 1);
            }
            ftApplyComplexDieValue(inputElement, showElement, maxValue);
            return;
        }

        clearSingleTapCommit();
        const snap = parseInt(inputElement.value, 10) || 0;
        pendingTapBase = snap;
        singleTapCommitTimer = setTimeout(() => {
            singleTapCommitTimer = null;
            pendingTapBase = null;
            inputElement.value = String(snap + 1);
            ftApplyComplexDieValue(inputElement, showElement, maxValue);
        }, FT_COMPLEX_DIE_SINGLE_TAP_COMMIT_MS);
    });

    dieElement.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        holdClearConsumedClick = false;
        clearLongPressTimer();
        longPressTimer = setTimeout(() => {
            longPressTimer = null;
            clearSingleTapCommit();
            inputElement.value = '0';
            ftApplyComplexDieValue(inputElement, showElement, maxValue);
            holdClearConsumedClick = true;
        }, FT_COMPLEX_DIE_LONG_PRESS_MS);
    });

    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((evtName) => {
        dieElement.addEventListener(evtName, () => {
            clearLongPressTimer();
        });
    });
});

// Clear-all button zeroes every die's input + badge. Guarded: the button only exists on pages that render
// the sidelog (the original accessed it unguarded and threw elsewhere).
const clearComplexDiceButton = document.getElementById('btn_clear_complex_dice');
if (clearComplexDiceButton) {
    clearComplexDiceButton.addEventListener('mousedown', () => {
        for (const sides of [4, 6, 8, 10, 12, 20]) {
            const input = document.getElementById(`die_${sides}_dice`) as HTMLInputElement | null;
            const show = document.getElementById(`die_${sides}_dice_show`);
            if (input) input.value = '0';
            if (show) show.textContent = '';
        }
    });
}

// ===========================================================================================
// Window attachment — preserve the public surface the original exposed (window.ftApplyComplexDieValue).
// Used only by the wiring here today; exported for parity / external reuse.
// ===========================================================================================
declare global {
    interface Window {
        ftApplyComplexDieValue: typeof ftApplyComplexDieValue;
    }
}

Object.assign(window, { ftApplyComplexDieValue });

export {};
